import { Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Text from "src/components/Displays/Text";
import { EV_PATHS } from "src/consts/Routes/Ev";
import { SESSION_KEYS } from "src/consts/session";
import { yupResolver } from "@hookform/resolvers/yup";
import SubmitButton from "src/components/Shared/SubmitButton";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import TermsInputsForm from "src/components/Forms/Ev/TermsInputsForm";
import { smartChargingEvLightOrder } from "src/api/smartChargingClients";
import { termsInputsFormSchema } from "src/schema/SmartCharging/termsInputsFormSchema";

export interface TermsFormValues {
  agb: boolean;
}

const Terms = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formSubmitDisabled, setFormSubmitDisabled] = useState(true);
  const [getTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);

  const form = useForm<TermsFormValues>({
    defaultValues: {
      agb: false,
    },
    resolver: yupResolver(termsInputsFormSchema),
  });

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (name === "agb") {
        if (form.getValues().agb === true) {
          setFormSubmitDisabled(false);
        } else {
          setFormSubmitDisabled(true);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: TermsFormValues) => {
    setIsLoading(true);
    const transferId = getTransferId();
    if (!transferId) return;

    try {
      const response = await smartChargingEvLightOrder({
        payload: {
          tosAccepted: true,
        },
        tid: transferId,
      });

      setTimeout(() => {
        setIsLoading(false);
        window.location.href = response.redirectUrl;
      }, 1000);
    } catch (e) {}
  };

  const navigateToCompList = () => {
    navigate(EV_PATHS.COMPATIBILITY);
  };

  return (
    <>
      <Stack gap={5}>
        <Text
          text={t("page.evTerms.pageTitle")}
          sx={(theme) => ({
            fontWeight: 500,
            fontSize: "32px",
            lineHeight: "40px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={t("page.evTerms.ListTitle")}
          sx={(theme) => ({
            fontSize: "14px",
            lineHeight: "24px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Stack>
          <li
            style={{
              color: "black",
              marginLeft: "30px",
            }}
          >
            <Text
              text={t("page.evTerms.ListItem1")}
              sx={(theme) => ({
                fontSize: "14px",
                lineHeight: "24px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </li>

          <li
            style={{
              color: "black",
              marginLeft: "30px",
            }}
          >
            <Text
              text={t("page.evTerms.ListItem2")}
              sx={(theme) => ({
                fontSize: "14px",
                lineHeight: "24px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </li>

          <li
            style={{
              color: "black",
              marginLeft: "30px",
            }}
          >
            <Text
              text={t("page.evTerms.ListItem3")}
              sx={(theme) => ({
                fontSize: "14px",
                lineHeight: "24px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </li>
        </Stack>

        <Stack
          gap={5}
          component={"form"}
          autoComplete="off"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <TermsInputsForm form={form} />
          <SubmitButton
            type="submit"
            isLoading={isLoading}
            disabled={formSubmitDisabled}
            buttonText={t("page.evTerms.formFields.submitButton")}
            sx={(theme) => ({
              fontSize: "16px",
              textTransform: "none",
              color: theme.palette.mainFont.primary,
              "&:disabled": {
                fontSize: "16px",
                cursor: "not-allowed",
                pointerEvents: "all !important",
                bgcolor: "rgba(0, 0, 0, 0.11)",
                color: isLoading ? undefined : theme.palette.grey[300],
              },
            })}
          />
        </Stack>

        <Stack>
          <PrimaryButton
            onClick={navigateToCompList}
            text={"Welche Autos sind kompatibel?"}
            sx={{
              border: "1px solid #00000038",
              backgroundColor: "transparent",
              color: "#3B3B3B",
            }}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default Terms;
